import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  LinearProgress,
  Button,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
  Avatar,
  Skeleton,
  Stack,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarMonthIcon,
  Speed as SpeedIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  CheckCircle as CheckCircleIcon,
  MoreHoriz as MoreHorizIcon,
  North as NorthIcon,
  Refresh as RefreshIcon,
  AssignmentLate as AssignmentLateIcon,
  Person as PersonIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { dashboardService, rapportiniService } from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
dayjs.locale('it');

const COLORS = ['#e50000', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4'];

// Componente StatCard moderno
const StatCard = ({ icon: Icon, value, label, sublabel, gradient, trend, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      height: '100%',
      background: gradient,
      borderRadius: 3,
      position: 'relative',
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.3s ease',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      } : {},
    }}
  >
    {/* Pattern decorativo */}
    <Box
      sx={{
        position: 'absolute',
        top: -30,
        right: -30,
        width: 120,
        height: 120,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: -20,
        right: 20,
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.05)',
      }}
    />

    <CardContent sx={{ position: 'relative', zIndex: 1, p: { xs: 2, sm: 2.5 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Icon sx={{ fontSize: 26, color: 'white' }} />
        </Box>
        {trend && (
          <Chip
            icon={<NorthIcon sx={{ fontSize: 14, color: 'white !important' }} />}
            label={trend}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
              '& .MuiChip-icon': { ml: 0.5 },
            }}
          />
        )}
      </Box>

      <Typography
        variant="h3"
        sx={{
          color: 'white',
          fontWeight: 800,
          fontSize: { xs: '1.75rem', sm: '2.25rem' },
          lineHeight: 1,
          mb: 0.5,
        }}
      >
        {value}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255,255,255,0.9)',
          fontWeight: 500,
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
        }}
      >
        {label}
      </Typography>

      {sublabel && (
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.7)',
            display: 'block',
            mt: 0.5,
          }}
        >
          {sublabel}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// Componente ChartCard moderno
const ChartCard = ({ title, subtitle, children, action }) => (
  <Paper
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: '#fafafa',
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && (
        <IconButton size="small" sx={{ color: 'text.secondary' }}>
          <MoreHorizIcon />
        </IconButton>
      )}
    </Box>
    <Box sx={{ p: 2 }}>
      {children}
    </Box>
  </Paper>
);

// Componente Empty State
const EmptyState = ({ message }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 6,
      color: 'text.secondary',
    }}
  >
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        bgcolor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
      }}
    >
      <TrendingUpIcon sx={{ fontSize: 32, color: '#ccc' }} />
    </Box>
    <Typography variant="body2">{message}</Typography>
  </Box>
);

// Tooltip personalizzato per i grafici
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: 'white',
          p: 1.5,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          border: '1px solid #eee',
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="subtitle2" fontWeight={700} color="#e50000">
          {payload[0].value} interventi
        </Typography>
      </Box>
    );
  }
  return null;
};

// Sezione "Rapportini mancanti" (solo admin / super_admin)
const RapportiniMancantiSection = ({ items, loading, onOpen }) => {
  const MAX_VISIBLE = 8;
  const count = items.length;
  const visibleItems = items.slice(0, MAX_VISIBLE);
  const remaining = count - visibleItems.length;
  const hasMissing = count > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 4,
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: hasMissing ? '#fecaca' : '#bbf7d0',
        borderLeft: '5px solid',
        borderLeftColor: hasMissing ? '#fd0000' : '#10b981',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          bgcolor: hasMissing ? '#fef2f2' : '#f0fdf4',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: hasMissing ? 'rgba(253,0,0,0.1)' : 'rgba(16,185,129,0.1)',
            }}
          >
            {hasMissing ? (
              <AssignmentLateIcon sx={{ color: '#fd0000', fontSize: 24 }} />
            ) : (
              <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
            )}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }} noWrap>
            Rapportini mancanti
          </Typography>
        </Box>
        {hasMissing && (
          <Chip
            label={count}
            size="small"
            sx={{
              bgcolor: '#fd0000',
              color: 'white',
              fontWeight: 800,
              fontSize: '0.8rem',
              height: 26,
              minWidth: 26,
            }}
          />
        )}
      </Box>

      {/* Body */}
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Box sx={{ py: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 2, mb: 1.5 }} />
            ))}
          </Box>
        ) : !hasMissing ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 30, color: '#10b981' }} />
            </Box>
            <Typography variant="body2" fontWeight={700} color="#166534">
              Tutti i rapportini sono stati compilati
            </Typography>
          </Box>
        ) : (
          <>
            {visibleItems.map((item) => (
              <Box
                key={item.id}
                onClick={() => onOpen(item.id)}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  mb: 1.5,
                  borderRadius: 2,
                  border: '1px solid #fecaca',
                  bgcolor: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#fd0000',
                    bgcolor: '#fff5f5',
                    boxShadow: '0 4px 12px rgba(253,0,0,0.12)',
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                      <Chip
                        icon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
                        label={item.data ? dayjs(item.data).format('DD/MM/YYYY') : '—'}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          bgcolor: '#fee2e2',
                          color: '#991b1b',
                          '& .MuiChip-icon': { color: '#991b1b' },
                        }}
                      />
                      {item.tipologia && (
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {item.tipologia}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      title={item.cantiere || ''}
                      sx={{
                        color: '#1a1a1a',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.cantiere || 'Cantiere non specificato'}
                    </Typography>
                    {item.cliente && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap title={item.cliente}>
                        {item.cliente}
                      </Typography>
                    )}
                  </Box>
                  <ChevronRightIcon sx={{ color: '#fd0000', flexShrink: 0 }} />
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Operatori coinvolti — in evidenza */}
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                  {item.capocantiere && (
                    <Chip
                      icon={<PersonIcon sx={{ fontSize: 15, color: 'white !important' }} />}
                      label={item.capocantiere}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        bgcolor: '#fd0000',
                        color: 'white',
                        '& .MuiChip-icon': { ml: 0.5 },
                      }}
                    />
                  )}
                  {(item.tecnici || []).map((tec) => (
                    <Chip
                      key={tec.id}
                      icon={<PersonIcon sx={{ fontSize: 15 }} />}
                      label={tec.nome}
                      size="small"
                      color="warning"
                      sx={{
                        height: 24,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: 'white',
                        '& .MuiChip-icon': { ml: 0.5, color: 'white' },
                      }}
                    />
                  ))}
                  {!item.capocantiere && (!item.tecnici || item.tecnici.length === 0) && (
                    <Typography variant="caption" color="text.secondary">
                      Nessun operatore assegnato
                    </Typography>
                  )}
                </Stack>
              </Box>
            ))}

            {remaining > 0 && (
              <Box
                onClick={() => onOpen(visibleItems[0]?.id)}
                sx={{
                  textAlign: 'center',
                  py: 1,
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                e altri {remaining}…
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sezione "Rapportini mancanti" — solo per admin/super_admin
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [rapportiniMancanti, setRapportiniMancanti] = useState([]);
  const [rapportiniMancantiLoading, setRapportiniMancantiLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadRapportiniMancanti();
  }, [isAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getStats();
      setData(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dati dashboard:', error);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const loadRapportiniMancanti = async () => {
    try {
      setRapportiniMancantiLoading(true);
      const response = await rapportiniService.getDaCompilare();
      setRapportiniMancanti(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Errore nel caricamento dei rapportini mancanti:', error);
      setRapportiniMancanti([]);
    } finally {
      setRapportiniMancantiLoading(false);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f8f9fa',
          pt: 3,
          pb: 4,
        }}
      >
        <Container maxWidth="xl">
          <Box mb={4}>
            <Skeleton variant="text" width={300} height={40} />
            <Skeleton variant="text" width={200} height={24} />
          </Box>
          <Grid container spacing={2} mb={4}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Grid item xs={6} sm={6} md={4} lg={2.4} key={i}>
                <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rounded" height={350} sx={{ borderRadius: 3 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rounded" height={350} sx={{ borderRadius: 3 }} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', maxWidth: 400 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <WarningIcon sx={{ fontSize: 40, color: '#e50000' }} />
          </Box>
          <Typography variant="h6" gutterBottom fontWeight={700}>
            Errore nel caricamento
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {error || 'Dati non disponibili'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{
              bgcolor: '#e50000',
              '&:hover': { bgcolor: '#c50000' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Riprova
          </Button>
        </Paper>
      </Box>
    );
  }

  // Data per l'ora corrente
  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? 'Buongiorno' : currentHour < 18 ? 'Buon pomeriggio' : 'Buonasera';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f9fa',
        pb: 4,
      }}
    >
      {/* Header con gradiente sottile */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          pt: { xs: 3, sm: 4 },
          pb: { xs: 8, sm: 10 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(229,0,0,0.3) 0%, transparent 70%)',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  mb: 0.5,
                }}
              >
                {greeting}, {user?.nome}!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                Ecco la panoramica della tua attività • {dayjs().format('dddd DD MMMM YYYY')}
              </Typography>
            </Box>

            <Box display="flex" gap={1}>
              <IconButton
                onClick={loadData}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Container principale con offset negativo per overlap */}
      <Container maxWidth="xl" sx={{ mt: { xs: -6, sm: -7 }, position: 'relative', zIndex: 2 }}>
        {/* Rapportini mancanti (#1) — solo admin / super_admin */}
        {isAdmin && (
          <RapportiniMancantiSection
            items={rapportiniMancanti}
            loading={rapportiniMancantiLoading}
            onOpen={(id) => navigate(`/interventi/${id}`)}
          />
        )}

        {/* KPI Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={6} md={4} lg={2.4}>
            <StatCard
              icon={BusinessIcon}
              value={data.kpi.total_clienti}
              label="Clienti Totali"
              gradient="linear-gradient(135deg, #e50000 0%, #b91c1c 100%)"
              onClick={() => navigate('/clienti')}
            />
          </Grid>

          <Grid item xs={6} sm={6} md={4} lg={2.4}>
            <StatCard
              icon={BuildIcon}
              value={data.kpi.cantieri_attivi}
              label="Cantieri Attivi"
              sublabel={`su ${data.kpi.total_cantieri} totali`}
              gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
              onClick={() => navigate('/cantieri')}
            />
          </Grid>

          <Grid item xs={6} sm={6} md={4} lg={2.4}>
            <StatCard
              icon={TodayIcon}
              value={data.kpi.interventi_oggi}
              label="Interventi Oggi"
              gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
              onClick={() => navigate('/interventi')}
            />
          </Grid>

          <Grid item xs={6} sm={6} md={4} lg={2.4}>
            <StatCard
              icon={CalendarMonthIcon}
              value={data.kpi.interventi_questo_mese}
              label="Interventi Mese"
              gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              onClick={() => navigate('/calendario')}
            />
          </Grid>

          <Grid item xs={6} sm={6} md={4} lg={2.4}>
            <StatCard
              icon={SpeedIcon}
              value={`${data.kpi.efficienza_media}%`}
              label="Efficienza Media"
              gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
            />
          </Grid>
        </Grid>

        {/* Grafici Principali */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Trend Area Chart */}
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Andamento Interventi"
              subtitle="Ultimi 30 giorni"
            >
              {data.charts.trend_interventi.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data.charts.trend_interventi}>
                    <defs>
                      <linearGradient id="colorInterventi" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#e50000" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#e50000" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="data"
                      tickFormatter={(value) => dayjs(value).format('DD/MM')}
                      stroke="#999"
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis stroke="#999" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="interventi"
                      stroke="#e50000"
                      strokeWidth={2.5}
                      fill="url(#colorInterventi)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState message="Nessun dato disponibile" />
              )}
            </ChartCard>
          </Grid>

          {/* Pie Chart Stati */}
          <Grid item xs={12} md={6} lg={4}>
            <ChartCard title="Stati Interventi">
              {data.charts.stati_interventi.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={data.charts.stati_interventi}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {data.charts.stati_interventi.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          stroke="none"
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        borderRadius: 8,
                        border: 'none',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState message="Nessun dato disponibile" />
              )}
              {/* Legenda personalizzata */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
                {data.charts.stati_interventi.map((entry, index) => (
                  <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: COLORS[index % COLORS.length],
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {entry.name} ({entry.value})
                    </Typography>
                  </Box>
                ))}
              </Box>
            </ChartCard>
          </Grid>
        </Grid>

        {/* Seconda riga grafici */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Tipologie */}
          <Grid item xs={12} md={6}>
            <ChartCard title="Tipologie Interventi">
              {data.charts.tipologie_interventi.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.charts.tipologie_interventi} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" stroke="#999" fontSize={12} tickLine={false} />
                    <YAxis stroke="#999" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: 'none',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      }}
                    />
                    <Bar dataKey="value" fill="#e50000" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState message="Nessun dato disponibile" />
              )}
            </ChartCard>
          </Grid>

          {/* Top Cantieri */}
          <Grid item xs={12} md={6}>
            <ChartCard title="Top 5 Cantieri per Interventi">
              {data.charts.top_cantieri.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.charts.top_cantieri} layout="vertical" barCategoryGap="15%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" stroke="#999" fontSize={12} tickLine={false} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={120}
                      stroke="#999"
                      fontSize={11}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: 'none',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      }}
                    />
                    <Bar dataKey="interventi" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState message="Nessun dato disponibile" />
              )}
            </ChartCard>
          </Grid>
        </Grid>

        {/* Terza riga - Progress e Liste */}
        <Grid container spacing={3}>
          {/* Progress Cantieri */}
          <Grid item xs={12} md={4}>
            <ChartCard title="Completamento Task">
              {data.charts.cantieri_progress.length > 0 ? (
                <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 1 }}>
                  {data.charts.cantieri_progress.map((cantiere, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        bgcolor: '#fafafa',
                        borderRadius: 2,
                        border: '1px solid #eee',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#e50000',
                          bgcolor: '#fff',
                        },
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          title={cantiere.name}
                          sx={{
                            // era noWrap con maxWidth 60%: fino a 391px di nome sparivano
                            // senza modo di leggerli. Due righe e poi i puntini.
                            minWidth: 0,
                            flex: 1,
                            mr: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {cantiere.name}
                        </Typography>
                        <Chip
                          label={`${cantiere.completamento}%`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            height: 22,
                            bgcolor: cantiere.completamento === 100 ? '#dcfce7' : '#fef3c7',
                            color: cantiere.completamento === 100 ? '#166534' : '#92400e',
                          }}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={cantiere.completamento}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: '#e5e7eb',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            bgcolor: cantiere.completamento === 100 ? '#10b981' : '#e50000',
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {cantiere.completate} di {cantiere.totali} task completate
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <EmptyState message="Nessun dato disponibile" />
              )}
            </ChartCard>
          </Grid>

          {/* Prossimi Interventi */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#fafafa',
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <AccessTimeIcon sx={{ color: '#e50000', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    Prossimi Interventi
                  </Typography>
                </Box>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate('/calendario')}
                  sx={{
                    color: '#e50000',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    '&:hover': { bgcolor: 'rgba(229,0,0,0.05)' },
                  }}
                >
                  Vedi tutti
                </Button>
              </Box>

              <Box sx={{ p: 2, maxHeight: 340, overflowY: 'auto' }}>
                {data.prossimi_interventi.length > 0 ? (
                  data.prossimi_interventi.map((intervento) => (
                    <Box
                      key={intervento.id}
                      onClick={() => navigate(`/interventi/${intervento.id}`)}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: 2,
                        border: '1px solid #eee',
                        bgcolor: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#e50000',
                          boxShadow: '0 4px 12px rgba(229,0,0,0.1)',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1a1a1a' }}>
                          {intervento.cantiere}
                        </Typography>
                        <Chip
                          label={intervento.stato}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            bgcolor: intervento.stato === 'programmato' ? '#dbeafe' : '#fef3c7',
                            color: intervento.stato === 'programmato' ? '#1e40af' : '#92400e',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {intervento.cliente}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                        <CalendarMonthIcon sx={{ fontSize: 14, color: '#e50000' }} />
                        <Typography variant="caption" fontWeight={600} color="#e50000">
                          {dayjs(intervento.data).format('ddd DD MMM')} • {intervento.ora_inizio?.substring(0, 5)}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <EmptyState message="Nessun intervento programmato" />
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Cantieri Critici */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: data.cantieri_critici.length > 0 ? '#fef2f2' : '#f0fdf4',
                }}
              >
                {data.cantieri_critici.length > 0 ? (
                  <WarningIcon sx={{ color: '#e50000', fontSize: 20 }} />
                ) : (
                  <CheckCircleOutlineIcon sx={{ color: '#10b981', fontSize: 20 }} />
                )}
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  {data.cantieri_critici.length > 0 ? 'Richiedono Attenzione' : 'Tutto OK!'}
                </Typography>
              </Box>

              <Box sx={{ p: 2, maxHeight: 340, overflowY: 'auto' }}>
                {data.cantieri_critici.length > 0 ? (
                  data.cantieri_critici.map((cantiere) => (
                    <Box
                      key={cantiere.id}
                      onClick={() => navigate(`/cantieri/${cantiere.id}`)}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: 2,
                        bgcolor: '#fef2f2',
                        border: '1px solid #fecaca',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#fee2e2',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} color="#991b1b" mb={1}>
                        {cantiere.nome}
                      </Typography>
                      {cantiere.giorni_ultimo_intervento && (
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                          <AccessTimeIcon sx={{ fontSize: 12, color: '#b91c1c' }} />
                          <Typography variant="caption" color="#b91c1c">
                            Ultimo intervento: {cantiere.giorni_ultimo_intervento} giorni fa
                          </Typography>
                        </Box>
                      )}
                      {cantiere.tasks_non_completate > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <WarningIcon sx={{ fontSize: 12, color: '#b91c1c' }} />
                          <Typography variant="caption" color="#b91c1c">
                            {cantiere.tasks_non_completate} task in sospeso ({cantiere.percentuale_non_completate}%)
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 6,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: '#dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ fontSize: 32, color: '#10b981' }} />
                    </Box>
                    <Typography variant="body2" fontWeight={600} color="#166534">
                      Tutti i cantieri sono aggiornati!
                    </Typography>
                    <Typography variant="caption" color="text.secondary" mt={0.5}>
                      Nessuna situazione critica rilevata
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;
